import {connect} from 'react-redux';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {withPush} from '../../../../../../../srcCommon/push/pushContext';
import {makeMapStateToProps} from '../../../../../../helpers/reselectHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {updateConversationTrackersAction} from '../../../../../../services/tasks/updateConversationTrackersAction';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../services/tracking';
import {AppState} from '../../../../../../store/appState';
import {
  makeGetConversationInboxes,
  makeGetConversationInboxTeammateIds,
  makeGetIsTeamConversation,
} from '../../../../../../store/data/conversations/conversationsSelectors';
import {
  subscribeToConversationsAction,
  unsubscribeFromConversationsAction,
} from '../../../../../../store/data/conversations/subscribeConversationsActions';
import {getIsUserVisitor, getUserId} from '../../../../../../store/data/teammates/teammatesSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {makeGetInviteParticipantsTrackers} from '../../../../../../store/stateless/inviteParticipantsSelectors';
import {InviteDropdownTrackers} from './inviteDropdownTrackers';

/*
 * Props.
 */

interface InviteDropdownTrackersContainerProps {
  conversation: ConversationModel;
  cellsContext: CellsContext;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.conversation;
}

function getCellsContext(_state: AppState, props: InviteDropdownTrackersContainerProps) {
  return props.cellsContext;
}

function getUserOrDelegatedTeammateId(_state: unknown, props: {cellsContext: CellsContext}) {
  return props.cellsContext.teammateId;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InviteDropdownTrackersContainerProps>()(() => {
  const getConversationInboxes = makeGetConversationInboxes(getConversation);
  const getConversationInboxesTeammateIds = makeGetConversationInboxTeammateIds(getConversationInboxes);
  const getVisualTrackers = makeGetInviteParticipantsTrackers(
    getUserOrDelegatedTeammateId,
    getConversationInboxesTeammateIds,
    getConversation,
  );

  return {
    userId: getUserId,
    visualTrackers: getVisualTrackers,
    conversationInboxes: getConversationInboxes,
    conversationInboxesTeammateIds: getConversationInboxesTeammateIds,
    isUserVisitor: getIsUserVisitor,
    isTeamConversation: makeGetIsTeamConversation(getConversationInboxes),
    cellsContext: getCellsContext,
  };
});

export const InviteDropdownTrackersContainer = withPush(
  connect(
    mapStateToProps,
    {updateConversationTrackersAction, subscribeToConversationsAction, unsubscribeFromConversationsAction},
    (stateProps, dispatchProps, ownProps) => {
      const {cellsContext} = stateProps;
      const {conversation} = ownProps;

      const removeTracker: HandlerOf<number> = (teammateId) => {
        dispatchProps.updateConversationTrackersAction(ownProps.conversation.id, {remove: [teammateId]});

        // Tracking.
        trackInterfaceEvent(InterfaceEventsEnum.SHARE_MENU, {step: 'remove_teammate'});
      };

      const onSubscribeClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.FOLLOW_CONVERSATION, {
          action: 'follow',
          teammate_id: cellsContext.teammateId,
        });

        dispatchProps.subscribeToConversationsAction(cellsContext, [conversation.id]);
      };

      const onUnsubscribeClick = () => {
        trackInterfaceEvent(InterfaceEventsEnum.FOLLOW_CONVERSATION, {
          action: 'unfollow',
          teammate_id: cellsContext.teammateId,
        });

        dispatchProps.unsubscribeFromConversationsAction(cellsContext, [conversation.id]);
      };

      return {
        ...stateProps,
        ...ownProps,
        removeTracker,
        onSubscribeClick,
        onUnsubscribeClick,
      };
    },
  )(InviteDropdownTrackers),
);
